import { useRef, useState, type ReactElement } from 'react';
import {
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Text,
  Box,
  LogoutIcon,
} from '@citibox-panel/ui';
import { useAuthContext } from '@citibox-panel/auth';
import { signOutOfPanel } from '@/lib/signOut';

function getInitials(name: string | null | undefined, email: string | null | undefined): string {
  if (name && name.trim().length > 0) {
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }
  return email ? email.charAt(0).toUpperCase() : '?';
}

export interface UserMenuProps {
  // Display names from /me, never the per-person extra permissions.
  roleNames: readonly string[];
}

export function UserMenu({ roleNames }: UserMenuProps): ReactElement | null {
  const { firebaseUser } = useAuthContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  if (firebaseUser === null) return null;

  const email = firebaseUser.email ?? '';
  const photoUrl = firebaseUser.photoURL;
  const displayName = firebaseUser.displayName;
  const initials = getInitials(displayName, email);

  const roles = roleNames.join(' · ');
  // The identity block cannot take focus inside role="menu", so the roles ride on the button's name.
  // TODO: i18n
  const accountLabel = roles === '' ? `Cuenta de ${email}` : `Cuenta de ${email} · ${roles}`;
  const handleSignOut = async (): Promise<void> => {
    await signOutOfPanel();
    setOpen(false);
  };

  return (
    <>
      <IconButton
        ref={buttonRef}
        onClick={() => { setOpen((v) => !v); }}
        aria-label={accountLabel}
        aria-haspopup="true"
        aria-expanded={open}
        size="small"
      >
        {/* exactOptionalPropertyTypes: only spread src when it's a real string */}
        <Avatar {...(photoUrl !== null ? { src: photoUrl } : {})}>
          {initials}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => { setOpen(false); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1, maxWidth: 320 }}>
          <Text variant="subtitle2">{displayName ?? email}</Text>
          <Text variant="caption" color="muted" component="div">
            {email}
          </Text>
          {roles !== '' && (
            <Text variant="caption" color="muted" component="div">
              {roles}
            </Text>
          )}
        </Box>
        <Divider />
        <MenuItem
          role="menuitem"
          onClick={() => {
            void handleSignOut();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Cerrar sesión
        </MenuItem>
      </Menu>
    </>
  );
}
